//@ts-nocheck
import React, { useState, useRef } from 'react';
import { useFormik } from 'formik';
import { Dayjs } from 'dayjs';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import validate, {
	PROFILE_IMAGE_MAX_BYTES,
	PROFILE_IMAGE_MAX_LABEL,
} from './helper/editPagesValidate';
import notificationState from '../../../shared/mobx/notification-state';
import Card, {
	CardActions,
	CardBody,
	CardFooter,
	CardHeader,
	CardLabel,
	CardSubTitle,
	CardTitle,
} from '../../../components/bootstrap/Card';
import Button from '../../../components/bootstrap/Button';
import Spinner from '../../../components/bootstrap/Spinner';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import CommonDesc from '../../../common/other/CommonDesc';
import Avatar from '../../../components/Avatar';
import USERS from '../../../common/data/userDummyData';
import auth from '../../../shared/mobx/auth-state';
import userState from '../../../shared/mobx/user-state';

const ProfilePage = () => {
	/**
	 * Common
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [lastSave, setLastSave] = useState<Dayjs | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [passwordChangeCTA, setPasswordChangeCTA] = useState<boolean>(false);

	const inputRef = useRef(null);

	const handleSave = async (values) => {
		try {
			if (passwordChangeCTA) {
				const status = await userState.changePassword(values);
				if(status) {
					formik.setFieldValue('currentPassword', '', false);
					formik.setFieldValue('newPassword', '', false);
					formik.setFieldValue('confirmPassword', '', false);
					setPasswordChangeCTA(false);
				}
			} else {
				await userState.updateProfile(values);
				if (inputRef.current) {
					inputRef.current.value = '';
				}
			}
		} finally {
			setIsLoading(false);
		}

	};

	const formik = useFormik({
		initialValues: {
			firstName: auth.currentUser?.firstName,
			lastName: auth.currentUser?.lastName,
			emailAddress: auth.currentUser?.email,
			currentPassword: '',
			newPassword: '',
			confirmPassword: '',
			profileImage: null
		},
		validate: values => {
			return validate(values, passwordChangeCTA);
		},
		onSubmit: (values) => {
			setIsLoading(true);
			handleSave(values);
		},
	});



	return (
		<PageWrapper title="My Profile">
			<Page>
				<div className='row h-100 align-content-start'>
					{/* <div className='col-12'>
						<div className='display-6 fw-bold py-2'>My Profile</div>
					</div> */}
					<div className='col-md-12 mt-2'>
						<Card>
							<CardBody>
								<div className='col-12'>
									<div className='row g-4 align-items-center'>
										<div className='col-lg-auto'>
											<Avatar
												srcSet={ auth.currentUser.profileImage || USERS.JOHN.srcSet}
												src={USERS.JOHN.src}
												color={USERS.JOHN.color}
												rounded={3}
											/>
										</div>
										<div className='col-lg'>
											<div className='row g-4'>
												<div className='col-auto'>
													<Input
														ref={inputRef}
														disabled={isLoading}
														type='file'
														autoComplete='photo'
														ariaLabel='Upload image file'
														accept="image/png, image/jpeg"
														onChange={(event) => {
															const input = event.currentTarget;
															const file = input.files ? input.files[0] : null;
															formik.setFieldError('profileImage', undefined);
															if (!file) {
																formik.setFieldValue('profileImage', null);
																return;
															}
															if (file.size > PROFILE_IMAGE_MAX_BYTES) {
																input.value = '';
																formik.setFieldValue('profileImage', null);
																const msg = `File size exceeds the ${PROFILE_IMAGE_MAX_LABEL} limit. Please choose a smaller image.`;
																formik.setFieldError('profileImage', msg);
																notificationState.addToasterError(msg);
																return;
															}
															formik.setFieldValue('profileImage', file);
														}}
													/>
												</div>
												{/* <div className='col-auto'>
													<Button disabled={isLoading} color='dark' isLight icon='Delete'>
														Delete Avatar
													</Button>
												</div> */}
												<div className='col-12'>
													<p className='lead text-muted mb-1'>
														Avatar helps your teammates get to know you.
													</p>
													<p className='small text-muted mb-1'>
														PNG or JPEG, maximum file size {PROFILE_IMAGE_MAX_LABEL}.
													</p>
													{formik.errors.profileImage && (
														<p className='small text-danger mb-0' role='alert'>
															{formik.errors.profileImage}
														</p>
													)}
												</div>
											</div>
										</div>
									</div>
								</div>
							</CardBody>
						</Card>
						<Card>
							<CardHeader>
								<CardLabel icon='Person' className='profile-svg'>
									<CardTitle tag='div' className='h5'>
										Personal Information
									</CardTitle>
									<CardSubTitle tag='div' className='h6'>
										User's credentials
									</CardSubTitle>
								</CardLabel>
							</CardHeader>
							<CardBody>
								<div className='row g-4'>
									<div className='col-md-6'>
										<FormGroup id='firstName' label='First Name' isFloating>
											<Input
												disabled={isLoading}
												placeholder='First Name'
												autoComplete='additional-name'
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												value={formik.values.firstName}
												isValid={formik.isValid}
												isTouched={formik.touched.firstName}
												invalidFeedback={formik.errors.firstName}
												validFeedback='Looks good!'
											/>
										</FormGroup>
									</div>
									<div className='col-md-6'>
										<FormGroup id='lastName' label='Last Name' isFloating>
											<Input
												disabled={isLoading}
												placeholder='Last Name'
												autoComplete='family-name'
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												value={formik.values.lastName}
												isValid={formik.isValid}
												isTouched={formik.touched.lastName}
												invalidFeedback={formik.errors.lastName}
												validFeedback='Looks good!'
											/>
										</FormGroup>
									</div>
								</div>
							</CardBody>
						</Card>
						<Card>
							<CardHeader>
								<CardLabel icon='Phonelink' className='profile-svg'>
									<CardTitle tag='div' className='h5'>
										Contact Information
									</CardTitle>
									<CardSubTitle tag='div' className='h6'>
										User's contact information
									</CardSubTitle>
								</CardLabel>
							</CardHeader>
							<CardBody>
								<div className='row g-4'>
									<div className='col-md-12'>
										<FormGroup
											id='emailAddress'
											label='Email address'
											isFloating>
											<Input
												type='email'
												placeholder='Email address'
												autoComplete='email'
												disabled={true}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												value={formik.values.emailAddress}
												isValid={formik.isValid}
												isTouched={formik.touched.emailAddress}
												invalidFeedback={formik.errors.emailAddress}
												validFeedback='Looks good!'
											/>
										</FormGroup>
									</div>
									{/* <div className='col-md-6'>
										<FormGroup id='phone' label='Phone Number' isFloating>
											<Input
												type='tel'
												placeholder='Phone Number'
												autoComplete='tel'
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												value={formik.values.phone}
												isValid={formik.isValid}
												isTouched={formik.touched.phone}
												invalidFeedback={formik.errors.phone}
												validFeedback='Looks good!'
											/>
										</FormGroup>
									</div> */}
								</div>
							</CardBody>
						</Card>
						<Card>
							<CardHeader>
								<CardLabel icon='LocalPolice' iconColor='primary'>
									<CardTitle tag='div' className='h5'>
										Password
									</CardTitle>
									<CardSubTitle tag='div' className='h6'>
										Password change operations
									</CardSubTitle>
								</CardLabel>
								<CardActions>
									{passwordChangeCTA ? (
										<Button
											disabled={isLoading}
											color='danger'
											isLight
											icon='Cancel'
											onClick={() => setPasswordChangeCTA(false)}>
											Cancel
										</Button>
									) : (
										<>
											<span>Do you want to change?</span>
											<Button
												disabled={isLoading}
												color='primary'
												isLight
												icon='PublishedWithChanges'
												onClick={() => setPasswordChangeCTA(true)}>
												Yes
											</Button>
										</>
									)}
								</CardActions>
							</CardHeader>
							{passwordChangeCTA && (
								<CardBody>
									<div className='row g-4'>
										<div className='col-12'>
											<FormGroup
												id='currentPassword'
												label='Current password'
												isFloating>
												<Input
													disabled={isLoading}
													type='password'
													placeholder='Current password'
													autoComplete='current-password'
													onChange={formik.handleChange}
													invalidFeedback={formik.errors.currentPassword}
													isTouched={formik.touched.currentPassword}
													value={formik.values.currentPassword}
													isValid={formik.isValid}
													onBlur={formik.handleBlur}
													validFeedback='Looks good!'

												/>
											</FormGroup>
										</div>
										<div className='col-12'>
											<FormGroup
												id='newPassword'
												label='New password'
												isFloating>
												<Input
													disabled={isLoading}
													type='password'
													placeholder='New password'
													autoComplete='new-password'
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													value={formik.values.newPassword}
													isValid={formik.isValid}
													isTouched={formik.touched.newPassword}
													invalidFeedback={formik.errors.newPassword}
													validFeedback='Looks good!'
												/>
											</FormGroup>
										</div>
										<div className='col-12'>
											<FormGroup
												id='confirmPassword'
												label='Confirm new password'
												isFloating>
												<Input
													disabled={isLoading}
													type='password'
													placeholder='Confirm new password'
													autoComplete='new-password'
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													value={formik.values.confirmPassword}
													isValid={formik.isValid}
													isTouched={formik.touched.confirmPassword}
													invalidFeedback={formik.errors.confirmPassword}
													validFeedback='Looks good!'
												/>
											</FormGroup>
										</div>
									</div>{' '}
								</CardBody>
							)}
							<CardFooter>
								<CommonDesc className='light-purple profile-detail'>
									<span className='text-white'>For your security, we recommend that you change your password
									every 3 months at most.</span>
								</CommonDesc>
							</CardFooter>
						</Card>
					</div>
				</div>
				<div className='row'>
					<div className='col-12'>
						<Card>
							<CardBody>
								<div className='row align-items-center'>
									<div className='col'>
										{lastSave ? (
											<>
												{/* <Icon
													icon='DoneAll'
													size='lg'
													className='me-2 text-muted'
												/>
												<span className='me-2 text-muted'>Last Saved</span>
												<strong>
													{dayjs(lastSave).format(
														'MMMM Do, YYYY - HH:mm',
													)}
												</strong> */}
											</>
										) : (
											<>
												{/* <Icon
													icon='Warning'
													size='lg'
													className='me-2 text-warning'
												/>
												<span className='text-warning'>Not saved yet</span> */}
											</>
										)}
									</div>
									<div className='col-auto'>
										<div className='row g-1'>
											<div className='col-auto'>
												<Button
													disabled={isLoading}
													className='me-3 light-purple'
													icon={isLoading ? undefined : 'Save'}
													isLight
													color={lastSave ? 'info' : 'success'}

													onClick={formik.handleSubmit}>
													{isLoading && <Spinner isSmall inButton />}
													{isLoading
														? (passwordChangeCTA && 'Changing') || 'Saving'
														: (passwordChangeCTA && 'Change Password') || 'Save'}
												</Button>
											</div>
											{/* <div className='col-auto'>
												<Dropdown direction='up'>
													<DropdownToggle hasIcon={false}>
														<Button
															color={themeStatus}
															icon='MoreVert'
															aria-label='More'
														/>
													</DropdownToggle>
													<DropdownMenu isAlignmentEnd>
														<DropdownItem>
															<Button
																className='me-3'
																icon='Save'
																isLight
																isDisable={isLoading}
																onClick={formik.resetForm}>
																Reset
															</Button>
														</DropdownItem>
													</DropdownMenu>
												</Dropdown>
											</div> */}
										</div>
									</div>
								</div>
							</CardBody>
						</Card>
					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};

export default ProfilePage;
